<template>
  <div class="login-page">
    <div class="scene-bg" aria-hidden="true" />

    <!-- 中心整体登录区 -->
    <div class="login-center">
      <div class="login-card">
        <div class="brand-identity">
          <div class="brand-main">智链商序</div>
          <div class="brand-en">Chain Trading System</div>
          <div class="brand-cn">—— 链式交易系统 ——</div>
          <div class="brand-slogan">「链可信 · 序自成」</div>
        </div>

        <div class="login-divider" />

        <div class="form-header">
          <h3 class="form-title">欢迎登录</h3>
        </div>

        <el-form
          ref="loginForm"
          :model="loginForm"
          :rules="loginRules"
          class="login-form"
          autocomplete="on"
          label-position="left"
          @submit.native.prevent
        >
          <el-form-item prop="username">
            <el-input
              ref="username"
              v-model="loginForm.username"
              placeholder="用户名"
              name="username"
              type="text"
              tabindex="1"
              autocomplete="on"
            >
              <i slot="prefix" class="el-input__icon el-icon-user" />
            </el-input>
          </el-form-item>

          <el-form-item prop="password">
            <el-input
              :key="passwordType"
              ref="password"
              v-model="loginForm.password"
              :type="passwordType"
              placeholder="密码"
              name="password"
              tabindex="2"
              autocomplete="on"
              @keyup.enter.native="handleLogin"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock" />
            </el-input>
            <span class="show-pwd" @click="showPwd">
              <i :class="passwordType === 'password' ? 'el-icon-view' : 'el-icon-hide'" />
            </span>
          </el-form-item>

          <el-button
            :loading="loading"
            type="primary"
            class="login-button"
            @click.native.prevent="handleLogin"
          >
            登 录
          </el-button>
        </el-form>
      </div>

      <footer class="page-footer">
        © {{ currentYear }} 智链商序 · Chain Trading System
      </footer>
    </div>

    <div v-if="showServerStatus" :class="['server-status', serviceHealth ? 'status-ok' : 'status-error']">
      <i :class="serviceHealth ? 'el-icon-success' : 'el-icon-error'" />
      <span>{{ serviceHealthMessage }}</span>
    </div>
  </div>
</template>

<script>
import api from '@/api'

export default {
  name: 'Login',
  data() {
    const validateUsername = (rule, value, callback) => {
      if (value.trim().length === 0) {
        callback(new Error('请输入用户名'))
      } else {
        callback()
      }
    }
    const validatePassword = (rule, value, callback) => {
      if (value.length === 0) {
        callback(new Error('请输入密码'))
      } else {
        callback()
      }
    }
    return {
      loginForm: {
        username: 'admin',
        password: 'admin'
      },
      loginRules: {
        username: [{ required: true, trigger: 'blur', validator: validateUsername }],
        password: [{ required: true, trigger: 'blur', validator: validatePassword }]
      },
      loading: false,
      passwordType: 'password',
      redirect: undefined,
      serviceHealth: true,
      serviceHealthMessage: '',
      showServerStatus: false
    }
  },
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  },
  watch: {
    $route: {
      handler: function(route) {
        this.redirect = route.query && route.query.redirect
      },
      immediate: true
    }
  },
  created() {
    this.checkServiceHealth()
  },
  methods: {
    checkServiceHealth() {
      api.health.checkHealth()
        .then(res => {
          this.serviceHealth = true
          this.serviceHealthMessage = res.data.message
          this.showServerStatus = true
          setTimeout(() => {
            this.showServerStatus = false
          }, 3000)
        })
        .catch(error => {
          console.error('健康检查失败:', error)
          this.serviceHealth = false
          this.serviceHealthMessage = '服务连接异常，请稍后重试'
          this.showServerStatus = true
        })
    },
    showPwd() {
      if (this.passwordType === 'password') {
        this.passwordType = ''
      } else {
        this.passwordType = 'password'
      }
      this.$nextTick(() => {
        this.$refs.password.focus()
      })
    },
    handleLogin() {
      this.$refs.loginForm.validate(valid => {
        if (valid) {
          this.loading = true

          api.user.login(this.loginForm)
            .then(res => {
              if (res.data && res.data.success === true) {
                const userData = res.data.data

                const userInfo = {
                  userId: userData.id,
                  username: userData.username,
                  signatureImagePath: userData.signatureImagePath || '',
                  role: userData.role,
                  roleType: userData.role_type,
                  unitId: userData.unit_id,
                  unitName: userData.unit_name,
                  unitType: userData.unit_type,
                  menus: userData.menus || []
                }

                const loginData = {
                  token: userData.token,
                  permissionCodes: userData.permissionCodes || [],
                  permissionTree: userData.permissionTree || [],
                  menuPermissions: userData.menuPermissions || [],
                  buttonPermissions: userData.buttonPermissions || [],
                  apiPermissions: userData.apiPermissions || []
                }

                this.$store.commit('SET_LOGIN_DATA', {
                  ...userInfo,
                  ...loginData
                })

                this.$store.dispatch('setUserInfo', userInfo)

                import('@/utils/auth').then(auth => {
                  auth.setUserInfo(userInfo)
                  import('@/router').then(({ initDynamicRoutes }) => {
                    try {
                      initDynamicRoutes()
                      this.$message.success(res.data.message || '登录成功')
                      this.loading = false

                      this.$nextTick(() => {
                        const targetPath = this.redirect || '/dashboard'
                        this.$router.push(targetPath).catch(err => {
                          console.error('路由跳转失败:', err)
                          this.$router.push('/dashboard')
                        })
                      })
                    } catch (error) {
                      console.error('初始化路由失败:', error)
                      this.$message.error('系统初始化失败，请刷新页面重试')
                      this.loading = false
                    }
                  }).catch(importError => {
                    console.error('导入路由模块失败:', importError)
                    this.$message.error('系统初始化失败，请刷新页面重试')
                    this.loading = false
                  })
                })
              } else {
                this.$message.error(res.data?.message || '登录失败，用户名或密码错误')
                this.loading = false
              }
            })
            .catch(error => {
              console.error('登录失败，错误详情:', error)
              this.$message.error(error.response?.data?.message || '登录失败，请检查用户名和密码')
              this.loading = false
            })
        } else {
          return false
        }
      })
    }
  }
}
</script>

<style lang="scss">
$brand-primary: #1A5F7A;

.login-page {
  .el-form-item {
    border: none;
    background: transparent;
    margin-bottom: 20px;
    position: relative;
  }

  .el-input {
    display: block;
    width: 100%;

    .el-input__inner {
      height: 44px;
      line-height: 44px;
      padding-left: 38px;
      border: 1px solid #dce3ea;
      border-radius: 8px;
      background: #f8fafb;
      color: #333;
      transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;

      &:hover {
        border-color: #b0c4cc;
        background: #fff;
      }

      &:focus {
        border-color: $brand-primary;
        background: #fff;
        box-shadow: 0 0 0 3px rgba(26, 95, 122, 0.1);
      }
    }

    .el-input__icon {
      line-height: 44px;
      color: #909399;
    }

    input:-webkit-autofill {
      box-shadow: 0 0 0 1000px #f8fafb inset !important;
      -webkit-text-fill-color: #333 !important;
    }
  }

  .el-form-item__error {
    color: #f56c6c;
    font-size: 12px;
    padding-top: 4px;
    position: absolute;
    top: 100%;
    left: 0;
  }
}
</style>

<style lang="scss" scoped>
$brand-primary: #1A5F7A;
$brand-secondary: #10A881;
$brand-base-size: 36px;

.login-page {
  position: relative;
  min-height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Source Han Sans SC", "Noto Sans SC", "PingFang SC", "Microsoft YaHei", sans-serif;
}

.scene-bg {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(180deg, rgba(7, 30, 40, 0.55) 0%, rgba(15, 51, 68, 0.45) 40%, rgba(7, 30, 40, 0.6) 100%),
    url('~@/assets/login_bg_supply_chain.png') center / cover no-repeat;
}

/* ── 中心登录卡片 ── */
.login-center {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  width: 100%;
  max-width: 480px;
}

.login-card {
  width: 100%;
  padding: 36px 40px 32px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
}

.brand-identity {
  text-align: center;

  .brand-main {
    font-size: $brand-base-size;
    font-weight: 700;
    color: $brand-primary;
    letter-spacing: 6px;
    line-height: 1.2;
  }

  .brand-en {
    font-size: $brand-base-size * 0.35;
    font-weight: 300;
    color: #888;
    letter-spacing: 2px;
    margin-top: 8px;
    text-transform: uppercase;
  }

  .brand-cn {
    font-size: $brand-base-size * 0.3;
    font-weight: 400;
    color: #555;
    margin-top: 10px;
    letter-spacing: 1px;
  }

  .brand-slogan {
    font-size: $brand-base-size * 0.25;
    font-weight: 300;
    font-style: italic;
    color: $brand-secondary;
    margin-top: 8px;
    letter-spacing: 1px;
  }
}

.login-divider {
  height: 1px;
  margin: 28px 0 24px;
  background: linear-gradient(90deg, transparent, #dce3ea, transparent);
}

.form-header {
  text-align: center;
  margin-bottom: 20px;

  .form-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a2332;
    margin: 0;
  }
}

.login-button {
  width: 100%;
  height: 44px;
  margin-top: 4px;
  font-size: 15px;
  letter-spacing: 6px;
  border-radius: 8px;
  background: linear-gradient(135deg, $brand-primary 0%, #1e7088 100%);
  border: none;

  &:hover,
  &:focus {
    background: linear-gradient(135deg, #164e64 0%, $brand-primary 100%);
  }
}

.show-pwd {
  position: absolute;
  right: 12px;
  bottom: 11px;
  font-size: 16px;
  color: #c0c4cc;
  cursor: pointer;
  user-select: none;

  &:hover { color: $brand-primary; }
}

.page-footer {
  margin-top: 20px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.65);
  letter-spacing: 0.5px;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}

.server-status {
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 10px 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 9999;

  &.status-ok {
    background-color: #f0f9eb;
    color: #67c23a;
    border: 1px solid #e1f3d8;
  }

  &.status-error {
    background-color: #fef0f0;
    color: #f56c6c;
    border: 1px solid #fde2e2;
  }

  i { margin-right: 5px; font-size: 16px; }
}

@media (max-width: 640px) {
  .login-card {
    padding: 28px 24px 24px;
  }

  .brand-identity .brand-main {
    font-size: 28px;
    letter-spacing: 4px;
  }
}
</style>
